<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import Dialog from 'primevue/dialog'
import DatePicker from 'primevue/datepicker'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import Button from '@/Components/Utils/Button.vue'
import { RadioButton, Textarea } from 'primevue'

type PaymentMethodOption = {
    label: string
    value: string
}

type EntryTypeOption = {
    label: string
    value: string
}

type EditableTransaction = {
    model_id: number | null
    entry_type: 'income' | 'expense'
    description: string
    amount: number
    payment_method: string
    transaction_date: string
}

const props = defineProps<{
    visible: boolean
    paymentMethodOptions: PaymentMethodOption[]
    entryTypeOptions: EntryTypeOption[]
    transaction?: EditableTransaction | null
    defaultTransactionDate: string
}>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()

const toDateObject = (value?: string | null): Date | null => {
    if (!value) return null

    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return null

    return new Date(year, month - 1, day)
}

const toDateString = (value?: Date | Date[] | null): string | null => {
    if (!value || Array.isArray(value)) return null

    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const defaultPaymentMethod = computed(() => props.paymentMethodOptions[0]?.value ?? 'cash')
const defaultEntryType = computed(() => props.entryTypeOptions[0]?.value ?? 'income')

const form = useForm({
    entry_type: defaultEntryType.value,
    description: '',
    amount: null as number | null,
    payment_method: defaultPaymentMethod.value,
    transaction_date: props.defaultTransactionDate,
})

const transactionDate = ref<Date | null>(toDateObject(props.defaultTransactionDate))

const resetForm = () => {
    form.clearErrors()
    form.entry_type = props.transaction?.entry_type ?? defaultEntryType.value
    form.description = props.transaction?.description ?? ''
    form.amount = props.transaction?.amount ?? null
    form.payment_method = props.transaction?.payment_method ?? defaultPaymentMethod.value
    form.transaction_date = props.transaction?.transaction_date ?? props.defaultTransactionDate
    transactionDate.value = toDateObject(form.transaction_date)
}

watch(
    () => [props.visible, props.transaction, props.defaultTransactionDate, props.paymentMethodOptions, props.entryTypeOptions],
    ([visible]) => {
        if (visible) {
            resetForm()
        }
    },
    { immediate: true }
)

const isEditing = computed(() => !!props.transaction?.model_id)
const modalTitle = computed(() => isEditing.value ? 'Ubah Pemasukan / Pengeluaran' : 'Tambah Pemasukan / Pengeluaran')
const getEntryTypeOptionClass = (value: string) => value === 'income' ? 'entry-type-option--income' : 'entry-type-option--expense'

const closeModal = () => {
    emit('update:visible', false)
}

const submit = () => {
    form.transaction_date = toDateString(transactionDate.value) ?? ''

    const options = {
        preserveScroll: true,
        onSuccess: () => {
            closeModal()
        },
    }

    if (props.transaction?.model_id) {
        form.patch(route('casheer.reports.update', props.transaction.model_id), options)
        return
    }

    form.post(route('casheer.reports.store'), options)
}
</script>

<template>
    <Dialog
        :visible="visible"
        modal
        :header="modalTitle"
        :style="{ width: 'min(92vw, 40rem)' }"
        @update:visible="emit('update:visible', $event)"
    >
        <div class="space-y-4">
            <p class="text-sm text-gray-500">
                Gunakan form ini untuk transaksi manual di luar pembayaran invoice.
            </p>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">

                <label class="text-sm text-gray-600">
                    <span class="mb-1 block">Jenis</span>
                    <div class="flex flex-wrap gap-6">
                        <div
                            v-for="typeOption in entryTypeOptions"
                            :key="typeOption.value"
                            :class="['entry-type-option flex items-center gap-2', getEntryTypeOptionClass(typeOption.value)]"
                        >
                            <RadioButton v-model="form.entry_type" :inputId="`entryType-${typeOption.value}`" name="entryType" :value="typeOption.value" />
                            <label :for="`entryType-${typeOption.value}`" class="font-medium">
                                {{ typeOption.label }}
                            </label>
                        </div>
                    </div>
                    <p v-if="form.errors.entry_type" class="mt-1 text-xs text-red-600">
                        *{{ form.errors.entry_type }}
                    </p>
                </label>

                <label class="text-sm text-gray-600">
                    <span class="mb-1 block">Tanggal Transaksi</span>
                    <DatePicker
                        v-model="transactionDate"
                        showIcon
                        fluid
                        dateFormat="dd/mm/yy"
                        inputClass="w-full"
                    />
                    <p v-if="form.errors.transaction_date" class="mt-1 text-xs text-red-600">
                        {{ form.errors.transaction_date }}
                    </p>
                </label>
            </div>

            <label class="block text-sm text-gray-600">
                <span class="mb-1 block">Deskripsi</span>
                <Textarea
                    v-model="form.description"
                    fluid
                    placeholder="Contoh: beli bahan, pemasukan lain, ongkir, dll."
                    rows="3"
                />
                <p v-if="form.errors.description" class="mt-1 text-xs text-red-600">
                    {{ form.errors.description }}
                </p>
            </label>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label class="text-sm text-gray-600">
                    <span class="mb-1 block">Jumlah</span>
                    <InputNumber
                        v-model="form.amount"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        fluid
                        :maxFractionDigits="0"
                        :min="1"
                        placeholder="Rp 0"
                    />
                    <p v-if="form.errors.amount" class="mt-1 text-xs text-red-600">
                        {{ form.errors.amount }}
                    </p>
                </label>

                <label class="text-sm text-gray-600">
                    <span class="mb-1 block">Metode Pembayaran</span>
                    <Select
                        v-model="form.payment_method"
                        :options="paymentMethodOptions"
                        optionLabel="label"
                        optionValue="value"
                        fluid
                        placeholder="Pilih metode"
                    />
                    <p v-if="form.errors.payment_method" class="mt-1 text-xs text-red-600">
                        {{ form.errors.payment_method }}
                    </p>
                </label>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <Button type="tertiary" label="Batal" @click="closeModal" />
                <Button
                    full
                    :type="isEditing ? 'save' : 'create'"
                    :label="isEditing ? 'Simpan Perubahan' : 'Tambah Transaksi'"
                    :loading="form.processing"
                    @click="submit"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.entry-type-option {
    transition: color 0.2s ease;
}

.entry-type-option--income {
    color: rgb(22 163 74);
}

.entry-type-option--expense {
    color: rgb(220 38 38);
}

.entry-type-option--income :deep(.p-radiobutton-box) {
    border-color: rgb(34 197 94);
}

.entry-type-option--expense :deep(.p-radiobutton-box) {
    border-color: rgb(239 68 68);
}

.entry-type-option--income :deep(.p-radiobutton-checked .p-radiobutton-box) {
    background: rgb(34 197 94);
    border-color: rgb(34 197 94);
}

.entry-type-option--expense :deep(.p-radiobutton-checked .p-radiobutton-box) {
    background: rgb(239 68 68);
    border-color: rgb(239 68 68);
}
</style>
